import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Armchair,
  Camera,
  Crop,
  Home,
  ImageIcon,
  MessageSquareText,
  Palette,
  Plus,
  Eye,
  Upload,
  Sparkles,
  Square,
  RectangleHorizontal,
  RectangleVertical,
} from 'lucide-react';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { deleteObject, getBlob, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import DesignAreaModalActions from '../components/DesignAreaModalActions';
import StyledRangeSlider from '../components/StyledRangeSlider';
import { useDesignAreaCropSnapshot } from '../hooks/useDesignAreaCropSnapshot';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useIsInsideAppLayout } from '../components/AppLayoutContext';
import DesignHexColorSection from '../components/DesignHexColorSection';
import NewDesignSelect from '../components/NewDesignSelect';
import VerticalAiBeforeAfterSlider from '../components/VerticalAiBeforeAfterSlider';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import { db, storage } from '../firebase';
import { API_BASE_URL, HAS_API_URL } from '../config/api';
import DesignOutputWorkspace from '../components/designWorkspace/DesignOutputWorkspace';
import DesignResultViewer from '../components/designWorkspace/DesignResultViewer';
import DesignWorkspaceShopSection from '../components/designWorkspace/DesignWorkspaceShopSection';
import ShopFurniturePanel from '../components/ShopFurniturePanel';
import ToolSeoSections from '../components/ToolSeoSections';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';
import { useDesignWorkspaceMode } from '../hooks/useDesignWorkspaceMode';
import { fetchProjectDoc } from '../lib/fetchProjectDoc';
import { requestGenerateDesign } from '../lib/requestGenerateDesign';
import { toolTypeFromDecorationLevel } from '../lib/projectToolType';
import { downloadUrlAsFile } from '../lib/downloadUrlAsFile';
import { resolveEditSourcePreviewUrl } from '../lib/projectWorkspaceUtils';
import { trackEvent } from '../utils/analytics';
import { exportCroppedRoomJpeg } from '../lib/exportRoomCrop';
import {
  CROP_MODES,
  CROP_MODE_KEYS,
  defaultCropModeFromDimensions,
} from '../lib/roomCropModes';
import {
  ArmchairIcon,
  BedIcon,
  BenchIcon,
  BookshelfIcon,
  CabinetIcon,
  ChairIcon,
  ChandelierIcon,
  CoffeeTableIcon,
  CurtainsIcon,
  DeleteIcon,
  DeskIcon,
  DiningTableIcon,
  LampIcon,
  MirrorIcon,
  PlantIcon,
  RugIcon,
  SideTableIcon,
  SofaIcon,
  TvUnitIcon,
  WallArtIcon,
  WardrobeIcon,
} from '../assets/furniture/react';
import bohoOriginalToolPreview from '../assets/design_tools/Boho_Original_H.webp';
import bohoFurnToolPreview from '../assets/design_tools/Boho_Furn_H.webp';
import modernToolPreview from '../assets/design_tools/Modern_H.webp';
import Seo from '../seo/Seo';
import { buildPublicToolPageSchemas } from '../seo/structuredData';
import { ROOM_TYPES as roomTypes, DESIGN_STYLES as designStyles } from '../lib/roomDesignOptions';

const CROP_MODE_ICONS = {
  square: Square,
  landscape: RectangleHorizontal,
  portrait: RectangleVertical,
};

const DECORATION_CHANGE_LEVELS = ['Light Refresh', 'Balanced Redesign', 'Full Decoration Redesign'];
const DECORATION_CHANGE_LEVEL_DEFAULT = 'Balanced Redesign';
const DECORATION_CHANGE_LEVEL_HINTS = {
  'Light Refresh': 'Swap furniture only. Keep walls and built-ins.',
  'Balanced Redesign': 'Refresh furniture and accents. Same general look.',
  'Full Decoration Redesign': 'Full décor and furniture changes.',
};

/** Matches room-interior tool hub cards (categoryToolHubs.js). */
const PAGE_HEADING_BY_DECORATION = {
  'Light Refresh': { prefix: 'Change Furniture ', accent: 'Only' },
  'Balanced Redesign': { prefix: 'Light Renovation ', accent: 'Upgrade' },
  'Full Decoration Redesign': { prefix: 'Full Decoration ', accent: 'Change' },
};

const WORKSPACE_TOOL_PREVIEW_BY_DECORATION = {
  'Full Decoration Redesign': {
    beforeImage: bohoOriginalToolPreview,
    afterImage: modernToolPreview,
    fallbackImage: modernToolPreview,
    alt: 'Full decoration and furniture example',
    hint: 'Explore a complete decor direction with updated furniture, colors, lighting, and styling.',
  },
  'Light Refresh': {
    beforeImage: bohoOriginalToolPreview,
    afterImage: bohoFurnToolPreview,
    fallbackImage: bohoFurnToolPreview,
    alt: 'Furniture change example',
    hint: 'Compare furniture-only ideas while your room layout, walls, and main structure stay familiar.',
  },
  'Balanced Redesign': {
    beforeImage: bohoOriginalToolPreview,
    afterImage: modernToolPreview,
    fallbackImage: modernToolPreview,
    alt: 'Light renovation example',
    hint: 'Preview refreshed colors, materials, and decor without committing to a full redesign.',
  },
};

const COLOR_THEMES = [
  'AI choice',
  'custom',
  'Neutral',
  'Warm',
  'White',
  'Dark',
  'Earth tones',
  'Blue',
  'Green',
];

const MAX_CUSTOM_COLORS = 3;
const MAX_WALL_COLORS = 2;

const WALL_COLOR_THEME_LIST = ['AI choice', 'custom'];

/** @param {string} raw */
function normalizeHexColor(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  const m = /^#?([0-9A-Fa-f]{6})$/.exec(s);
  if (!m) return '';
  return `#${m[1].toUpperCase()}`;
}

/** @param {unknown} raw */
function sanitizeHydratedSelectedColors(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const x of raw) {
    const h = normalizeHexColor(typeof x === 'string' ? x : '');
    if (h && !out.includes(h)) out.push(h);
    if (out.length >= MAX_CUSTOM_COLORS) break;
  }
  return out;
}

/** @param {unknown} raw */
function sanitizeHydratedSelectedWallColors(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const x of raw) {
    const h = normalizeHexColor(typeof x === 'string' ? x : '');
    if (h && !out.includes(h)) out.push(h);
    if (out.length >= MAX_WALL_COLORS) break;
  }
  return out;
}

const FURNITURE_CONFIG = [
  { name: 'Sofa', Icon: SofaIcon },
  { name: 'Chair', Icon: ChairIcon },
  { name: 'Armchair', Icon: ArmchairIcon },
  { name: 'Bed', Icon: BedIcon },
  { name: 'Chandelier', Icon: ChandelierIcon },
  { name: 'Dining Table', Icon: DiningTableIcon },
  { name: 'Coffee Table', Icon: CoffeeTableIcon },
  { name: 'Side Table', Icon: SideTableIcon },
  { name: 'Desk', Icon: DeskIcon },
  { name: 'TV Unit', Icon: TvUnitIcon },
  { name: 'Wardrobe', Icon: WardrobeIcon },
  { name: 'Cabinet', Icon: CabinetIcon },
  { name: 'Bookshelf', Icon: BookshelfIcon },
  { name: 'Rug', Icon: RugIcon },
  { name: 'Lamp', Icon: LampIcon },
  { name: 'Mirror', Icon: MirrorIcon },
  { name: 'Wall Art', Icon: WallArtIcon },
  { name: 'Plant', Icon: PlantIcon },
  { name: 'Curtains', Icon: CurtainsIcon },
  { name: 'Bench', Icon: BenchIcon },
];

const MAX_FURNITURE_QTY = 6;
const NOTES_MAX = 250;

const GENERATED_IMAGE_QUALITY_STANDARD = 'standard';
const GENERATED_IMAGE_QUALITY_HIGH = 'high';
const DEFAULT_GENERATED_IMAGE_QUALITY = GENERATED_IMAGE_QUALITY_STANDARD;
const GENERATED_IMAGE_QUALITY_OPTIONS = [
  { value: GENERATED_IMAGE_QUALITY_STANDARD, label: 'Standard image quality', cost: 1 },
  { value: GENERATED_IMAGE_QUALITY_HIGH, label: 'High image quality', cost: 3 },
];
const GENERATED_IMAGE_QUALITY_DISPLAY = {
  [GENERATED_IMAGE_QUALITY_STANDARD]: 'Standard',
  [GENERATED_IMAGE_QUALITY_HIGH]: 'High Quality',
};

function totalFurnitureQuantity(items) {
  return items.reduce((sum, row) => sum + (row.quantity || 0), 0);
}

function furnitureIconForName(name) {
  const found = FURNITURE_CONFIG.find((f) => f.name === name);
  return found ? found.Icon : null;
}

const FURNITURE_ALLOW = new Set(FURNITURE_CONFIG.map((f) => f.name));

/** @returns {'square'|'landscape'|'portrait'|null} */
function coerceCropModeFromDoc(raw) {
  if (typeof raw !== 'string') return null;
  const key = raw.trim();
  return CROP_MODE_KEYS.includes(key) ? key : null;
}

function pickListedValue(saved, list, fallback) {
  const s = typeof saved === 'string' ? saved.trim() : '';
  return list.includes(s) ? s : fallback;
}

/**
 * Normalize Firestore furniture rows to match Furniture Builder names and qty cap.
 * @param {unknown} raw
 * @returns {{ name: string; quantity: number }[]}
 */
function sanitizeHydratedSelectedItems(raw) {
  if (!Array.isArray(raw)) return [];
  const merged = new Map();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const name =
      typeof entry.name === 'string' ? entry.name.trim() : '';
    if (!name || !FURNITURE_ALLOW.has(name)) continue;
    let q = Number(entry.quantity);
    if (!Number.isFinite(q)) q = 0;
    q = Math.min(8, Math.max(0, Math.floor(q)));
    if (q <= 0) continue;
    merged.set(name, (merged.get(name) || 0) + q);
  }
  let total = 0;
  const out = [];
  for (const [name, qty] of merged) {
    const remaining = 8 - total;
    if (remaining <= 0) break;
    const add = Math.min(qty, remaining);
    if (add > 0) {
      out.push({ name, quantity: add });
      total += add;
    }
  }
  return out;
}

function GenerateWaitingIndicator({ submitPhase }) {
  return (
    <div className="generate-waiting-indicator" role="status" aria-live="polite">
      <div className="generate-waiting-text">
        {submitPhase === 'generate'
          ? 'Generating your room design… please wait'
          : 'Preparing generation…'}
      </div>
      <div className="generate-progress-track" aria-hidden="true">
        <div className="generate-progress-fill" />
      </div>
    </div>
  );
}

function NewDesignGenerateReviewSummary({
  selectedRoomType,
  selectedStyle,
  decorationChangeLevel,
  generatedImageQuality,
  generatedImageCreditCost,
}) {
  return (
    <dl className="new-design-review-summary">
      <div className="new-design-review-summary-row">
        <dt>Room type</dt>
        <dd>{selectedRoomType}</dd>
      </div>
      <div className="new-design-review-summary-row">
        <dt>Design style</dt>
        <dd>{selectedStyle}</dd>
      </div>
      <div className="new-design-review-summary-row">
        <dt>Decoration level</dt>
        <dd>
          <span className="new-design-review-summary-desc">
            {DECORATION_CHANGE_LEVEL_HINTS[decorationChangeLevel]}
          </span>
        </dd>
      </div>
      <div className="new-design-review-summary-row">
        <dt>Image quality</dt>
        <dd>
          {GENERATED_IMAGE_QUALITY_DISPLAY[generatedImageQuality] ??
            GENERATED_IMAGE_QUALITY_DISPLAY[GENERATED_IMAGE_QUALITY_STANDARD]}
        </dd>
      </div>
      <div className="new-design-review-summary-row">
        <dt>Credit cost</dt>
        <dd>
          {generatedImageCreditCost}{' '}
          {generatedImageCreditCost === 1 ? 'Credit' : 'Credits'}
        </dd>
      </div>
    </dl>
  );
}

function NewDesignPage({ publicSeo = null, initialDecoration = null }) {
  const isInsideAppLayout = useIsInsideAppLayout();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const initialCropModeOverrideRef = useRef(null);
  const { user } = useAuth();
  const credits = useCredits();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editFromUrl = useMemo(() => {
    const raw = searchParams.get('edit');
    return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
  }, [searchParams]);

  const {
    pageMode,
    resultViewMode,
    setResultViewMode,
    currentProject,
    enterOutputMode,
    enterEditMode,
    resetToDesign,
    isEditMode,
  } = useDesignWorkspaceMode();
  const [shopOpen, setShopOpen] = useState(false);
  const [shopItemCount, setShopItemCount] = useState(0);
  const [downloadError, setDownloadError] = useState('');
  const [zoomedImage, setZoomedImage] = useState(
    /** @returns {{ src: string; alt: string } | null} */
    () => null,
  );

  const [hydratedEditId, setHydratedEditId] = useState(null);
  const [editHydration, setEditHydration] = useState(
    /** @returns {'idle'|'loading'|'ready'} */
    () => (searchParams.get('edit')?.trim() ? 'loading' : 'idle'),
  );
  const [selectedRoomType, setSelectedRoomType] = useState(roomTypes[0]);
  const [selectedStyle, setSelectedStyle] = useState(designStyles[0]);
  const [decorationChangeLevel, setDecorationChangeLevel] = useState(() => {
    const decorationParam = searchParams.get('decoration');
    return DECORATION_CHANGE_LEVELS.includes(decorationParam)
      ? decorationParam
      : initialDecoration || publicSeo?.initialDecoration || DECORATION_CHANGE_LEVEL_DEFAULT;
  });
  const publicToolPageClass = publicSeo?.path
    ? `new-design-page--${publicSeo.path.split('/').pop()}`
    : '';
  const uploadHeading = publicSeo?.uploadHeading ?? 'Upload Room Photo';
  const uploadDropzone = publicSeo?.uploadDropzone ?? 'Upload your room photo';
  const uploadHelper = publicSeo?.uploadHelper ?? null;
  const generateCta = publicSeo?.generateCta ?? 'Generate design';
  const noImageError = publicSeo?.noImageError ?? 'Please choose a room photo first.';
  const activeDesignTool = publicSeo?.designTool ?? 'room-interior';
  const activeToolType =
    publicSeo?.toolType ?? toolTypeFromDecorationLevel(decorationChangeLevel);
  const [useAiColorTheme, setUseAiColorTheme] = useState(true);
  const [selectedColors, setSelectedColors] = useState(
    /** @returns {string[]} */
    () => [],
  );
  const [themeColorSource, setThemeColorSource] = useState(
    /** @returns {'ai'|'custom'} */
    () => 'ai',
  );
  const [colorsSectionDirty, setColorsSectionDirty] = useState(false);
  const hydratedColorThemeRef = useRef('AI choice');
  const [useAiWallColorTheme, setUseAiWallColorTheme] = useState(true);
  const [selectedWallColors, setSelectedWallColors] = useState(
    /** @returns {string[]} */
    () => [],
  );
  const [wallColorSource, setWallColorSource] = useState(
    /** @returns {'ai'|'custom'} */
    () => 'ai',
  );
  const [wallsSectionDirty, setWallsSectionDirty] = useState(false);
  const hydratedWallColorThemeRef = useRef('AI choice');
  const [selectedItems, setSelectedItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [generatedImageQuality, setGeneratedImageQuality] = useState(DEFAULT_GENERATED_IMAGE_QUALITY);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState('');
  const croppedPreviewGenRef = useRef(0);
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const [cropMode, setCropMode] = useState('square');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [hasValidCrop, setHasValidCrop] = useState(false);
  const cropPixelsRef = useRef(null);
  const [submitPhase, setSubmitPhase] = useState('');
  const [error, setError] = useState('');
  const [creditsError, setCreditsError] = useState(false);
  const [themeColorsPopupOpen, setThemeColorsPopupOpen] = useState(false);
  const [wallColorsPopupOpen, setWallColorsPopupOpen] = useState(false);
  const [furniturePopupOpen, setFurniturePopupOpen] = useState(false);
  const furnitureDraftRef = useRef(null);
  const themeColorSectionRef = useRef(null);
  const wallColorSectionRef = useRef(null);
  const [cropPopupOpen, setCropPopupOpen] = useState(false);
  const cropPopupOpenRef = useRef(cropPopupOpen);
  cropPopupOpenRef.current = cropPopupOpen;
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false);
  const submitting = submitPhase !== '';
  const selectedRoomTypeRef = useRef(selectedRoomType);
  const selectedStyleRef = useRef(selectedStyle);
  const decorationChangeLevelRef = useRef(decorationChangeLevel);
  const editHydrationProjectIdRef = useRef(null);
  selectedRoomTypeRef.current = selectedRoomType;
  selectedStyleRef.current = selectedStyle;
  decorationChangeLevelRef.current = decorationChangeLevel;

  const cropConfig = CROP_MODES[cropMode] || CROP_MODES.square;

  const totalQty = useMemo(() => totalFurnitureQuantity(selectedItems), [selectedItems]);
  const atFurnitureCap = totalQty >= MAX_FURNITURE_QTY;
  const generatedImageCreditCost =
    generatedImageQuality === GENERATED_IMAGE_QUALITY_HIGH ? 3 : 1;
  const hasInsufficientCredits =
    credits !== null && credits < generatedImageCreditCost;

  const selectedQtyByName = useMemo(() => {
    const m = new Map();
    selectedItems.forEach((r) => m.set(r.name, r.quantity || 0));
    return m;
  }, [selectedItems]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (croppedPreviewUrl) URL.revokeObjectURL(croppedPreviewUrl);
    };
  }, [croppedPreviewUrl]);

  const refreshCroppedPreview = useCallback(async () => {
    const pixels = cropPixelsRef.current;
    if (!previewUrl || !pixels?.width || !pixels?.height) {
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return;
    }
    const gen = ++croppedPreviewGenRef.current;
    try {
      const cfg = CROP_MODES[cropMode] || CROP_MODES.square;
      const blob = await exportCroppedRoomJpeg(
        previewUrl,
        pixels,
        cfg.outWidth,
        cfg.outHeight,
        0.82,
      );
      if (gen !== croppedPreviewGenRef.current) return;
      const url = URL.createObjectURL(blob);
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      if (gen !== croppedPreviewGenRef.current) return;
      setCroppedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  }, [previewUrl, cropMode]);

  useEffect(() => {
    if (!zoomedImage) return undefined;
    function handleEsc(event) {
      if (event.key === 'Escape') setZoomedImage(null);
    }
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [zoomedImage]);


  const handleFurnitureCancel = useCallback(() => {
    if (Array.isArray(furnitureDraftRef.current)) {
      setSelectedItems(furnitureDraftRef.current.map((item) => ({ ...item })));
    }
    furnitureDraftRef.current = null;
    setFurniturePopupOpen(false);
  }, []);

  const handleFurnitureDone = useCallback(() => {
    furnitureDraftRef.current = null;
    setFurniturePopupOpen(false);
  }, []);

  useEffect(() => {
    if (!themeColorsPopupOpen && !wallColorsPopupOpen && !furniturePopupOpen && !reviewPopupOpen) {
      return undefined;
    }
    function handleEsc(event) {
      if (event.key !== 'Escape') return;
      if (submitting) return;
      if (furniturePopupOpen) {
        handleFurnitureCancel();
        return;
      }
      if (reviewPopupOpen) {
        setReviewPopupOpen(false);
        return;
      }
      setThemeColorsPopupOpen(false);
      setWallColorsPopupOpen(false);
    }
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [
    themeColorsPopupOpen,
    wallColorsPopupOpen,
    furniturePopupOpen,
    reviewPopupOpen,
    submitting,
    handleFurnitureCancel,
  ]);

  const themeColorButtonCaption = useMemo(() => {
    if (themeColorSource === 'custom' && selectedColors.length > 0) return null;
    if (themeColorSource === 'ai') return 'AI Choice';
    return 'Add custom colors';
  }, [themeColorSource, selectedColors.length]);

  const wallColorButtonCaption = useMemo(() => {
    if (wallColorSource === 'custom' && selectedWallColors.length > 0) return null;
    if (wallColorSource === 'ai') return 'AI Choice';
    return 'Add custom wall colors';
  }, [wallColorSource, selectedWallColors.length]);

  const furnitureItemsSummary = useMemo(() => {
    if (selectedItems.length === 0) {
      return 'Add furniture';
    }
    return `${totalQty} item${totalQty === 1 ? '' : 's'} selected (max ${MAX_FURNITURE_QTY})`;
  }, [selectedItems.length, totalQty]);

  const pageHeading = useMemo(
    () =>
      PAGE_HEADING_BY_DECORATION[decorationChangeLevel] ??
      PAGE_HEADING_BY_DECORATION[DECORATION_CHANGE_LEVEL_DEFAULT],
    [decorationChangeLevel],
  );

  const workspaceToolPreview = useMemo(
    () =>
      WORKSPACE_TOOL_PREVIEW_BY_DECORATION[decorationChangeLevel] ??
      WORKSPACE_TOOL_PREVIEW_BY_DECORATION[DECORATION_CHANGE_LEVEL_DEFAULT],
    [decorationChangeLevel],
  );

  const showOutputResultViewer = pageMode === 'output' && currentProject != null;
  const editSourcePreviewUrl = resolveEditSourcePreviewUrl({
    croppedPreviewUrl,
    previewUrl,
    project: currentProject,
  });
  const showWorkspaceResultPanel = Boolean(
    (showOutputResultViewer || isEditMode) && currentProject,
  );
  const showWorkspaceToolGuide =
    !showWorkspaceResultPanel && !hydratedEditId && !editSourcePreviewUrl && !submitting;

  const activeProjectId =
    (currentProject?.id && String(currentProject.id)) || hydratedEditId || null;

  const handleDownloadImage = useCallback(
    async (url, baseName) => {
      setDownloadError('');
      try {
        await downloadUrlAsFile(url, baseName, user);
      } catch (err) {
        setDownloadError(err?.message || 'Download failed.');
      }
    },
    [user],
  );

  const handleShopItemsReady = useCallback(({ count }) => {
    setShopItemCount(count);
  }, []);

  const handleOutputEdit = useCallback(() => {
    if (currentProject?.id) {
      setHydratedEditId(String(currentProject.id));
    }
    enterEditMode();
  }, [currentProject, enterEditMode]);

  const resolveGeneratedProject = useCallback(
    async (projectId, gen, draftExtras) => {
      if (gen.project && typeof gen.project === 'object') {
        return { id: projectId, ...gen.project };
      }
      const fromDb = await fetchProjectDoc(projectId);
      if (fromDb) return fromDb;
      return {
        id: projectId,
        generatedImageUrl: gen.generatedImageUrl || '',
        status: 'completed',
        toolType: activeToolType,
        designTool: activeDesignTool,
        ...draftExtras,
      };
    },
    [activeDesignTool, activeToolType],
  );

  const pageTitle = (
    <h1 className="tool-hub-heading">
      {hydratedEditId ? (
        'Edit your Room design'
      ) : publicSeo?.h1 ? (
        publicSeo.h1
      ) : (
        <>
          {pageHeading.prefix}
          <span className="new-design-page-heading-accent">{pageHeading.accent}</span>
        </>
      )}
    </h1>
  );

  const openThemeColorsPopup = useCallback(() => {
    setWallColorsPopupOpen(false);
    setFurniturePopupOpen(false);
    setThemeColorsPopupOpen(true);
  }, []);

  const openWallColorsPopup = useCallback(() => {
    setThemeColorsPopupOpen(false);
    setFurniturePopupOpen(false);
    setWallColorsPopupOpen(true);
  }, []);

  const openFurniturePopup = useCallback(() => {
    setThemeColorsPopupOpen(false);
    setWallColorsPopupOpen(false);
    furnitureDraftRef.current = selectedItems.map((item) => ({ ...item }));
    setFurniturePopupOpen(true);
  }, [selectedItems]);

  const handleCropComplete = useCallback(
    (_area, areaPixels) => {
      cropPixelsRef.current = areaPixels;
      const valid = Boolean(areaPixels?.width > 0 && areaPixels?.height > 0);
      setHasValidCrop(valid);
      if (valid) {
        void refreshCroppedPreview();
      } else {
        setCroppedPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return '';
        });
      }
    },
    [refreshCroppedPreview],
  );

  const {
    openCropModal,
    setCropModalSnapshot,
    cancelCropModal,
    clearSnapshot,
  } = useDesignAreaCropSnapshot({
    cropMode,
    crop,
    zoom,
    hasValidCrop,
    croppedPreviewUrl,
    cropPixelsRef,
    setCropMode,
    setCrop,
    setZoom,
    setHasValidCrop,
    setCroppedPreviewUrl,
    setCropPopupOpen,
  });

  const handleCropDone = useCallback(() => {
    clearSnapshot();
    setCropPopupOpen(false);
    void refreshCroppedPreview();
  }, [clearSnapshot, refreshCroppedPreview]);

  const handleDimensionLoad = useCallback((event) => {
    const img = event.currentTarget;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setNaturalWidth(w);
    setNaturalHeight(h);
    const overrideMode = initialCropModeOverrideRef.current;
    initialCropModeOverrideRef.current = null;
    const nextMode =
      overrideMode &&
      CROP_MODES[overrideMode] &&
      CROP_MODE_KEYS.includes(overrideMode)
        ? overrideMode
        : defaultCropModeFromDimensions(w, h);
    setCropMode(nextMode);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    if (cropPopupOpenRef.current) {
      setCropModalSnapshot({
        cropMode: nextMode,
        crop: { x: 0, y: 0 },
        zoom: 1,
        cropPixels: null,
        hasValidCrop: false,
        croppedPreviewUrl: '',
      });
    }
  }, [setCropModalSnapshot]);

  const handleCropModeChange = useCallback((mode) => {
    setCropMode(mode);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  const applyUploadedImageFile = useCallback((nextFile, { openCropPopup = true } = {}) => {
    if (!nextFile || !nextFile.type.startsWith('image/')) {
      return;
    }
    setError('');
    setNaturalWidth(0);
    setNaturalHeight(0);
    setHasValidCrop(false);
    cropPixelsRef.current = null;
    croppedPreviewGenRef.current += 1;
    setCroppedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(nextFile);
    });
    setFile(nextFile);
    if (openCropPopup) {
      setCropPopupOpen(true);
    }
  }, []);

  const handleFileChange = useCallback((nextFile) => {
    applyUploadedImageFile(nextFile, { openCropPopup: true });
    trackEvent('room_image_uploaded', {
      room_type: selectedRoomTypeRef.current,
      design_style: selectedStyleRef.current,
      decoration_level: decorationChangeLevelRef.current,
    });
  }, [applyUploadedImageFile]);

  useEffect(() => {
    if (!editFromUrl) {
      editHydrationProjectIdRef.current = null;
      setHydratedEditId(null);
      setEditHydration('idle');
      initialCropModeOverrideRef.current = null;
      return undefined;
    }
    if (editHydrationProjectIdRef.current === editFromUrl) {
      return undefined;
    }
    let cancelled = false;

    async function hydrateEditProject() {
      setHydratedEditId(null);
      if (!user?.uid) return;
      setEditHydration('loading');
      try {
        const snap = await getDoc(doc(db, 'projects', editFromUrl));
        if (cancelled) return;
        if (!snap.exists()) {
          navigate('/projects', { replace: true });
          return;
        }
        const data = snap.data();
        if (data.userId !== user.uid) {
          navigate('/projects', { replace: true });
          return;
        }
        const originalUrl =
          typeof data.originalImageUrl === 'string'
            ? data.originalImageUrl.trim()
            : '';
        if (!originalUrl) {
          navigate('/projects', { replace: true });
          return;
        }

        const storagePath = `users/${user.uid}/projects/${editFromUrl}/original`;
        const storageObjRef = ref(storage, storagePath);

        const projectOriginalApiPath = `/api/projects/${encodeURIComponent(editFromUrl)}/original`;
        const projectOriginalApiUrl =
          HAS_API_URL && API_BASE_URL
            ? process.env.NODE_ENV === 'development'
              ? projectOriginalApiPath
              : `${API_BASE_URL}${projectOriginalApiPath}`
            : null;

        /** @type {Blob | null} */
        let blob = null;

        if (projectOriginalApiUrl) {
          try {
            const idToken = await user.getIdToken(true);
            const apiRes = await fetch(projectOriginalApiUrl, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            if (apiRes.ok) {
              const apiBlob = await apiRes.blob();
              if (apiBlob && apiBlob.size) {
                blob = apiBlob;
              }
            }
          } catch (apiErr) {
            blob = null;
          }
        }

        if (!blob || !blob.size) {
          try {
            blob = await getBlob(storageObjRef);
          } catch (storageErr) {
            blob = null;
          }
        }

        if (!blob || !blob.size) {
          let res;
          try {
            res = await fetch(originalUrl);
          } catch (netErr) {
            throw netErr;
          }
          if (!res.ok) throw new Error('Could not load project image.');
          blob = await res.blob();
        }

        if (cancelled) return;
        if (!blob.size) throw new Error('Project image was empty.');
        const type =
          blob.type && blob.type.startsWith('image/')
            ? blob.type
            : 'image/jpeg';
        const ext = type.includes('png') ? '.png' : '.jpg';
        const file = new File([blob], `room${ext}`, { type });

        setSelectedRoomType(
          pickListedValue(data.roomType, roomTypes, roomTypes[0]),
        );
        setSelectedStyle(
          pickListedValue(data.style, designStyles, designStyles[0]),
        );
        setDecorationChangeLevel(
          pickListedValue(
            data.decorationChangeLevel,
            DECORATION_CHANGE_LEVELS,
            DECORATION_CHANGE_LEVEL_DEFAULT,
          ),
        );
        const ct = pickListedValue(data.colorTheme, COLOR_THEMES, 'AI choice');
        hydratedColorThemeRef.current = ct;
        setColorsSectionDirty(false);
        const loadedColors = sanitizeHydratedSelectedColors(data.selectedColors);
        if (loadedColors.length > 0) {
          setSelectedColors(loadedColors);
          setUseAiColorTheme(false);
          setThemeColorSource('custom');
        } else if (typeof data.useAiColorTheme === 'boolean') {
          setSelectedColors([]);
          setUseAiColorTheme(data.useAiColorTheme);
          setThemeColorSource(data.useAiColorTheme ? 'ai' : 'custom');
        } else {
          setSelectedColors([]);
          setUseAiColorTheme(true);
          setThemeColorSource('ai');
        }

        const wct = pickListedValue(
          data.wallColorTheme,
          WALL_COLOR_THEME_LIST,
          'AI choice',
        );
        hydratedWallColorThemeRef.current = wct;
        setWallsSectionDirty(false);
        const loadedWallColors = sanitizeHydratedSelectedWallColors(data.selectedWallColors);
        if (loadedWallColors.length > 0) {
          setSelectedWallColors(loadedWallColors);
          setUseAiWallColorTheme(false);
          setWallColorSource('custom');
        } else if (typeof data.useAiWallColorTheme === 'boolean') {
          setSelectedWallColors([]);
          setUseAiWallColorTheme(data.useAiWallColorTheme);
          setWallColorSource(data.useAiWallColorTheme ? 'ai' : 'custom');
        } else {
          setSelectedWallColors([]);
          setUseAiWallColorTheme(true);
          setWallColorSource('ai');
        }

        setSelectedItems(sanitizeHydratedSelectedItems(data.selectedItems));
        setNotes(
          typeof data.notes === 'string'
            ? data.notes.trim().slice(0, NOTES_MAX)
            : '',
        );

        const savedCrop = coerceCropModeFromDoc(data.cropMode);
        initialCropModeOverrideRef.current = savedCrop;

        applyUploadedImageFile(file, { openCropPopup: true });

        editHydrationProjectIdRef.current = editFromUrl;
        setHydratedEditId(editFromUrl);
        setEditHydration('ready');
      } catch (err) {
        console.error('[edit-project] hydrate failed:', {
          name: err?.name,
          message: err?.message,
        });
        if (!cancelled) {
          setError(err?.message || 'Could not load this project for editing.');
          setEditHydration('idle');
          navigate('/projects', { replace: true });
        }
      }
    }

    hydrateEditProject();
    return () => {
      cancelled = true;
    };
  }, [editFromUrl, user, navigate, applyUploadedImageFile]);

  const handleInputChange = (event) => {
    const [next] = event.target.files || [];
    handleFileChange(next);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const [next] = event.dataTransfer.files || [];
    handleFileChange(next);
  };

  const handleFurnitureTileClick = (name) => {
    if (atFurnitureCap) return;
    setSelectedItems((prev) => {
      if (totalFurnitureQuantity(prev) >= MAX_FURNITURE_QTY) return prev;
      const idx = prev.findIndex((r) => r.name === name);
      if (idx === -1) {
        return [...prev, { name, quantity: 1 }];
      }
      const next = [...prev];
      next[idx] = { name, quantity: next[idx].quantity + 1 };
      return next;
    });
  };

  const adjustQuantity = (name, delta) => {
    setSelectedItems((prev) => {
      const idx = prev.findIndex((r) => r.name === name);
      if (idx === -1) return prev;
      const row = prev[idx];
      const nextQty = row.quantity + delta;
      if (nextQty <= 0) {
        return prev.filter((r) => r.name !== name);
      }
      if (delta > 0 && totalFurnitureQuantity(prev) >= MAX_FURNITURE_QTY) {
        return prev;
      }
      const next = [...prev];
      next[idx] = { name, quantity: nextQty };
      return next;
    });
  };

  const removeItem = (name) => {
    setSelectedItems((prev) => prev.filter((r) => r.name !== name));
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value.slice(0, NOTES_MAX));
  };

  const markColorsSectionTouched = useCallback(() => {
    setColorsSectionDirty(true);
  }, []);

  const markWallsSectionTouched = useCallback(() => {
    setWallsSectionDirty(true);
  }, []);

  const handleThemeSourceChange = useCallback((src) => {
    setThemeColorSource(src);
    if (src === 'ai') {
      setUseAiColorTheme(true);
      setSelectedColors([]);
    } else {
      setUseAiColorTheme(false);
    }
  }, []);

  const handleThemeColorsChange = useCallback((next) => {
    setSelectedColors(next);
    if (next.length > 0) setUseAiColorTheme(false);
  }, []);

  const handleWallSourceChange = useCallback((src) => {
    setWallColorSource(src);
    if (src === 'ai') {
      setUseAiWallColorTheme(true);
      setSelectedWallColors([]);
    } else {
      setUseAiWallColorTheme(false);
    }
  }, []);

  const handleWallColorsChange = useCallback((next) => {
    setSelectedWallColors(next);
    if (next.length > 0) setUseAiWallColorTheme(false);
  }, []);

  const handleSave = async () => {
    if (!user?.uid) {
      navigate(`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
      return;
    }
    if (!file) {
      setError(noImageError);
      return;
    }
    const pixels = cropPixelsRef.current;
    if (!pixels || !previewUrl || !hasValidCrop) {
      setError('Adjust the crop under your uploaded photo, then try again.');
      return;
    }
    if (submitting) return;

    setError('');
    setCreditsError(false);
    setReviewPopupOpen(false);
    setSubmitPhase('upload');

    trackEvent('design_generation_started', {
      room_type: selectedRoomType,
      design_style: selectedStyle,
      decoration_level: decorationChangeLevel,
      image_quality: generatedImageQuality,
    });

    try {
      const cfg = CROP_MODES[cropMode] || CROP_MODES.square;
      const croppedBlob = await exportCroppedRoomJpeg(
        previewUrl,
        pixels,
        cfg.outWidth,
        cfg.outHeight,
      );
      const croppedFile = new File([croppedBlob], 'room.jpg', { type: 'image/jpeg' });

      const itemsForDoc = selectedItems
        .filter((r) => r.quantity > 0)
        .map((r) => ({ name: r.name, quantity: r.quantity }));

      const colorThemeForDoc =
        selectedColors.length > 0
          ? 'custom'
          : !colorsSectionDirty
            ? hydratedColorThemeRef.current
            : 'AI choice';
      const useAiForDoc = selectedColors.length === 0 && useAiColorTheme;
      const selectedColorsForDoc = selectedColors.slice(0, MAX_CUSTOM_COLORS);

      const wallColorThemeForDoc =
        selectedWallColors.length > 0
          ? 'custom'
          : !wallsSectionDirty
            ? hydratedWallColorThemeRef.current
            : 'AI choice';
      const useAiWallForDoc = selectedWallColors.length === 0 && useAiWallColorTheme;
      const selectedWallColorsForDoc = selectedWallColors.slice(0, MAX_WALL_COLORS);

      if (hydratedEditId) {
        const projectRef = doc(db, 'projects', hydratedEditId);
        const storagePath = `users/${user.uid}/projects/${hydratedEditId}/original`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, croppedFile);
        const originalImageUrl = await getDownloadURL(storageRef);

        await updateDoc(projectRef, {
          roomType: selectedRoomType,
          style: selectedStyle,
          decorationChangeLevel,
          colorTheme: colorThemeForDoc,
          useAiColorTheme: useAiForDoc,
          selectedColors: selectedColorsForDoc,
          wallColorTheme: wallColorThemeForDoc,
          useAiWallColorTheme: useAiWallForDoc,
          selectedWallColors: selectedWallColorsForDoc,
          selectedItems: itemsForDoc,
          notes: notes.trim().slice(0, NOTES_MAX),
          originalImageUrl,
          cropMode,
          inputOpenAiSize: cfg.openAiSize,
          originalImageWidth: naturalWidth || undefined,
          originalImageHeight: naturalHeight || undefined,
          generatedImageUrl: '',
          status: 'uploaded',
          errorMessage: deleteField(),
        });

        if (!HAS_API_URL) {
          setError(
            'The design server is not configured. Set REACT_APP_API_URL in your environment to generate and save projects.',
          );
          return;
        }

        setSubmitPhase('generate');
        const regenGen = await requestGenerateDesign(user, hydratedEditId, {
          regenerate: true,
          imageQuality: generatedImageQuality,
        });
        if (!regenGen.ok) {
          if (regenGen.code === 'INSUFFICIENT_CREDITS') {
            setCreditsError(true);
          } else {
            setError(regenGen.error);
          }
          return;
        }
        trackEvent('design_generation_completed', {
          room_type: selectedRoomType,
          design_style: selectedStyle,
          decoration_level: decorationChangeLevel,
          image_quality: generatedImageQuality,
          project_id: hydratedEditId,
        });

        const regenProject = await resolveGeneratedProject(hydratedEditId, regenGen, {
          roomType: selectedRoomType,
          style: selectedStyle,
          decorationChangeLevel,
          originalImageUrl,
        });
        enterOutputMode(regenProject);
        return;
      }

      if (!HAS_API_URL) {
        setError(
          'The design server is not configured. Set REACT_APP_API_URL in your environment to generate and save projects.',
        );
        return;
      }

      const projectId = doc(collection(db, 'projects')).id;
      const storagePathNew = `users/${user.uid}/projects/${projectId}/original`;
      const storageRef = ref(storage, storagePathNew);

      console.log('[NewDesign] draft upload started');
      await uploadBytes(storageRef, croppedFile);
      const originalImageUrlNew = await getDownloadURL(storageRef);

      const projectDraft = {
        designTool: activeDesignTool,
        toolType: activeToolType,
        roomType: selectedRoomType,
        style: selectedStyle,
        decorationChangeLevel,
        colorTheme: colorThemeForDoc,
        useAiColorTheme: useAiForDoc,
        selectedColors: selectedColorsForDoc,
        wallColorTheme: wallColorThemeForDoc,
        useAiWallColorTheme: useAiWallForDoc,
        selectedWallColors: selectedWallColorsForDoc,
        selectedItems: itemsForDoc,
        notes: notes.trim().slice(0, NOTES_MAX),
        originalImageUrl: originalImageUrlNew,
        cropMode,
        inputOpenAiSize: cfg.openAiSize,
        ...(naturalWidth ? { originalImageWidth: naturalWidth } : {}),
        ...(naturalHeight ? { originalImageHeight: naturalHeight } : {}),
      };

      console.log('[NewDesign] project creation skipped until generation success');

      setSubmitPhase('generate');
      const gen = await requestGenerateDesign(user, projectId, {
        imageQuality: generatedImageQuality,
        pendingCreate: true,
        projectDraft,
      });
      if (!gen.ok) {
        if (gen.code === 'INSUFFICIENT_CREDITS') {
          setCreditsError(true);
        } else if (gen.code === 'PROJECT_LIMIT_REACHED') {
          setError(gen.error);
        } else {
          setError(gen.error);
        }
        try {
          await deleteObject(storageRef);
        } catch (delErr) {
          if (delErr?.code !== 'storage/object-not-found') {
            console.error('[NewDesign] failed to delete draft original after generate error', delErr);
          }
        }
        return;
      }

      trackEvent('design_generation_completed', {
        room_type: selectedRoomType,
        design_style: selectedStyle,
        decoration_level: decorationChangeLevel,
        image_quality: generatedImageQuality,
        project_id: projectId,
      });

      const completedProject = await resolveGeneratedProject(projectId, gen, {
        ...projectDraft,
        originalImageUrl: originalImageUrlNew,
      });
      enterOutputMode(completedProject);
      setHydratedEditId(projectId);
    } catch (err) {
      setError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setSubmitPhase('');
    }
  };

  const editRouteBlocking = Boolean(editFromUrl) && editHydration !== 'ready';

  return (
    <>
      <Seo
        title={
          publicSeo?.title ||
          (hydratedEditId
            ? 'Edit AI Room Design | MyRoomsAI'
            : 'Create New AI Room Design | MyRoomsAI')
        }
        description={
          publicSeo?.description ||
          (hydratedEditId
            ? 'Update your room design brief and regenerate an AI interior concept for this project.'
            : 'Upload your room photo, choose style and furniture preferences, and generate a new AI interior design concept.')
        }
        canonicalPath={
          publicSeo?.path ||
          (initialDecoration === 'Full Decoration Redesign'
            ? '/projects/ai-room-decoration'
            : '/projects/ai-furniture-replacement')
        }
        noindex={!publicSeo}
        structuredData={
          publicSeo
            ? [...buildPublicToolPageSchemas(publicSeo), getSeoFaqSchema(publicSeo.path)].filter(Boolean)
            : []
        }
      />
      {!isInsideAppLayout ? <Navbar /> : null}
      <main
        className={`dashboard-page new-design-page ${publicToolPageClass}${
          pageMode === 'output' ? ' project-detail-page' : ''
        }`.trim()}
      >
        <div className="container">
          {editHydration === 'loading' ? (
            <p className="project-page-muted" role="status">
              Loading project…
            </p>
          ) : null}

          {error ? (
            <p className="dashboard-inline-error" role="alert">
              {error}
            </p>
          ) : null}

          {creditsError ? (
            <p className="dashboard-inline-error" role="alert">
              You do not have enough credits.{' '}
              <Link to="/credits">Buy a credit pack</Link> to continue.
            </p>
          ) : null}

          {pageMode === 'output' && currentProject ? (
            <>
              {shopOpen && currentProject.generatedImageUrl && activeProjectId ? (
                <div className="project-result-content">
                  <div className="shop-furniture-panel-wrap project-result-shop-panel">
                    <ShopFurniturePanel
                      projectId={activeProjectId}
                      generatedImageUrl={String(currentProject.generatedImageUrl)}
                      onClose={() => setShopOpen(false)}
                    />
                  </div>
                  {downloadError ? (
                    <p className="dashboard-inline-error project-detail-download-error" role="alert">
                      {downloadError}
                    </p>
                  ) : null}
                </div>
              ) : (
                <DesignOutputWorkspace
                  project={currentProject}
                  resultViewMode={resultViewMode}
                  onResultViewModeChange={setResultViewMode}
                  onNewProject={resetToDesign}
                  onEdit={handleOutputEdit}
                  onOpenShop={() => setShopOpen(true)}
                  shopItemCount={shopItemCount}
                  onDownloadImage={handleDownloadImage}
                  onZoomImage={setZoomedImage}
                  downloadError={downloadError}
                />
              )}

              <DesignWorkspaceShopSection
                project={currentProject}
                projectId={activeProjectId || ''}
                generatedImageUrl={
                  currentProject.generatedImageUrl ? String(currentProject.generatedImageUrl) : ''
                }
                shopOpen={shopOpen}
                onOpenShop={() => setShopOpen(true)}
                onItemsReady={handleShopItemsReady}
              />
            </>
          ) : (
          <section
            className={`dashboard-grid new-design-workspace design-dual-layout${
              isEditMode ? ' edit-mode' : ''
            }${!showWorkspaceResultPanel ? ' new-design-workspace--preview-heading' : ''}`}
          >
            <article className="card new-design-setup-card">
                <>
              <div className="new-design-upload-mobile-summary">
                <h3 className="new-design-subheading new-design-upload-section-heading">
                  <Upload
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  {uploadHeading}
                </h3>
                <input
                  ref={fileInputRef}
                  id="room-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="new-design-file-input"
                />
                <input
                  ref={cameraInputRef}
                  id="room-camera-capture"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleInputChange}
                  className="new-design-file-input"
                />
                {!previewUrl ? (
                  <div className="new-design-upload-empty-stack">
                    <div className="new-design-upload-dropzone-wrap">
                      <label
                        htmlFor="room-upload"
                        className="upload-dropzone"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                      >
                        <div className="upload-dropzone-inner">
                          <span className="upload-dropzone-cloud" aria-hidden="true">
                            <Upload size={38} strokeWidth={1.75} />
                          </span>
                          <span>{uploadDropzone}</span>
                        </div>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline new-design-camera-action"
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <Camera size={18} strokeWidth={2} aria-hidden="true" />
                      Take Photo
                    </button>
                  </div>
                ) : (
                  <>
                    <img
                      src={previewUrl}
                      alt=""
                      onLoad={handleDimensionLoad}
                      className="new-design-dims-probe"
                      decoding="async"
                      loading="lazy"
                    />
                    <div className="new-design-upload-mobile-preview">
                      <img
                        src={previewUrl}
                        alt="Uploaded room preview"
                        className="new-design-upload-mobile-preview-img"
                        decoding="async"
                      />
                    </div>
                    <div className="new-design-upload-mobile-summary-btns">
                      <div className="new-design-upload-summary-btn-row">
                        <button
                          type="button"
                          className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <ImageIcon size={15} strokeWidth={2} aria-hidden="true" />
                          Change Photo
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn"
                          onClick={openCropModal}
                        >
                          <Crop size={15} strokeWidth={2} aria-hidden="true" />
                          Change Design Area
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn new-design-camera-action"
                        onClick={() => cameraInputRef.current?.click()}
                      >
                        <Camera size={17} strokeWidth={2} aria-hidden="true" />
                        Take Photo
                      </button>
                    </div>
                  </>
                )}
              </div>

              {previewUrl && cropPopupOpen ? (
                    <div
                      className="new-design-theme-popup-backdrop"
                      role="presentation"
                      onClick={cancelCropModal}
                    >
                      <div
                        className="new-design-crop-popup"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Select design area"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="new-design-crop-panel-head">
                          <h3 className="new-design-subheading">
                            <Crop
                              className="new-design-subheading-icon"
                              size={18}
                              strokeWidth={2}
                              aria-hidden
                            />
                            Select design area
                          </h3>
                        </div>
                        <p className="new-design-hint">
                          Zoom in/out and then drag to frame the design area.
                        </p>
                        <div className="new-design-crop-mode-row" role="group" aria-label="Crop aspect ratio">
                          {CROP_MODE_KEYS.map((key) => {
                            const CropModeIcon = CROP_MODE_ICONS[key];
                            return (
                            <button
                              key={key}
                              type="button"
                              className={`new-design-chip new-design-crop-mode-btn${
                                cropMode === key ? ' new-design-chip--selected' : ''
                              }`}
                              aria-pressed={cropMode === key}
                              aria-label={CROP_MODES[key].label}
                              title={CROP_MODES[key].label}
                              onClick={() => handleCropModeChange(key)}
                            >
                              <CropModeIcon size={17} strokeWidth={2} aria-hidden="true" />
                            </button>
                            );
                          })}
                        </div>
                        <div className="new-design-crop-stage">
                          <Cropper
                            image={previewUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={cropConfig.aspect}
                            cropShape="rect"
                            showGrid={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleCropComplete}
                          />
                        </div>
                        <div className="new-design-crop-zoom">
                          <span className="new-design-crop-zoom-label">Zoom</span>
                          <StyledRangeSlider
                            min={1}
                            max={3}
                            step={0.02}
                            value={zoom}
                            aria-label="Crop zoom"
                            onChange={(e) => setZoom(Number(e.target.value))}
                          />
                        </div>
                        <DesignAreaModalActions
                          onCancel={cancelCropModal}
                          onDone={handleCropDone}
                        />
                      </div>
                    </div>
              ) : null}

              <div className="new-design-setup-fields">
                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <Home
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    New Room Type
                  </h3>
                  <NewDesignSelect
                    value={selectedRoomType}
                    onChange={setSelectedRoomType}
                    options={roomTypes}
                  />
                </div>

                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <Eye
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Design Style
                  </h3>
                  <div className="new-design-style-row">
                    <NewDesignSelect
                      className="new-design-style-select"
                      value={selectedStyle}
                      onChange={setSelectedStyle}
                      options={designStyles}
                    />
                  </div>
                </div>
              </div>

              <div className="new-design-control-group new-design-colors-combined-group">
                <h3 className="new-design-subheading new-design-colors-combined-heading">
                  <Palette
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Colors
                </h3>
                <div className="new-design-colors-combined-grid">
                  <div className="new-design-colors-combined-item">
                    <span className="new-design-colors-combined-label" id="new-design-theme-colors-trigger-label">
                      Theme
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost new-design-customize-popup-trigger new-design-customize-popup-trigger--color-picker"
                      aria-haspopup="dialog"
                      aria-expanded={themeColorsPopupOpen}
                      aria-controls="new-design-theme-colors-popup"
                      aria-labelledby="new-design-theme-colors-trigger-label"
                      onClick={openThemeColorsPopup}
                    >
                      {themeColorSource === 'custom' && selectedColors.length > 0 ? (
                        <span
                          className="new-design-color-swatches new-design-color-swatches--in-button"
                          aria-label="Selected custom theme colors"
                        >
                          {selectedColors.map((color) => (
                            <span
                              key={`theme-${color}`}
                              className="new-design-color-swatch"
                              style={{ backgroundColor: color }}
                              title={color}
                              aria-label={color}
                            />
                          ))}
                        </span>
                      ) : null}
                      {themeColorButtonCaption ? (
                        <span className="new-design-customize-trigger-caption">{themeColorButtonCaption}</span>
                      ) : null}
                    </button>
                  </div>
                  <div className="new-design-colors-combined-item">
                    <span className="new-design-colors-combined-label" id="new-design-wall-colors-trigger-label">
                      Walls
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost new-design-customize-popup-trigger new-design-customize-popup-trigger--color-picker"
                      aria-haspopup="dialog"
                      aria-expanded={wallColorsPopupOpen}
                      aria-controls="new-design-wall-colors-popup"
                      aria-labelledby="new-design-wall-colors-trigger-label"
                      onClick={openWallColorsPopup}
                    >
                      {wallColorSource === 'custom' && selectedWallColors.length > 0 ? (
                        <span
                          className="new-design-color-swatches new-design-color-swatches--in-button"
                          aria-label="Selected custom wall colors"
                        >
                          {selectedWallColors.map((color) => (
                            <span
                              key={`wall-${color}`}
                              className="new-design-color-swatch"
                              style={{ backgroundColor: color }}
                              title={color}
                              aria-label={color}
                            />
                          ))}
                        </span>
                      ) : null}
                      {wallColorButtonCaption ? (
                        <span className="new-design-customize-trigger-caption">{wallColorButtonCaption}</span>
                      ) : null}
                    </button>
                  </div>
                </div>
                <p className="new-design-field-helper">
                  Set theme and wall colors, or use AI Choice.
                </p>
              </div>

              <div className="new-design-control-group">
                <div className="new-design-customize-trigger-block">
                  <h3
                    id="new-design-furniture-trigger-label"
                    className="new-design-subheading"
                  >
                    <Armchair
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Extra Furniture
                  </h3>
                  <button
                    type="button"
                    className="btn btn-ghost new-design-customize-popup-trigger new-design-items-builder-title"
                    aria-haspopup="dialog"
                    aria-expanded={furniturePopupOpen}
                    aria-controls="new-design-furniture-popup"
                    aria-labelledby="new-design-furniture-trigger-label"
                    onClick={openFurniturePopup}
                  >
                    <span className="new-design-theme-colors-summary new-design-theme-colors-summary--in-button">
                      {furnitureItemsSummary}
                    </span>
                    <Plus
                      className="new-design-items-builder-add-mark"
                      size={14}
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  </button>
                  <p className="new-design-field-helper">
                    Add optional furniture items for your redesign.
                  </p>
                </div>
              </div>

              <div className="new-design-review-notes-group">
                <h3 className="new-design-subheading">
                  <MessageSquareText
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Notes & instructions for the AI
                </h3>

                <textarea
                  className="dashboard-notes new-design-notes"
                  rows={2}
                  maxLength={NOTES_MAX}
                  placeholder="Example: Change the floor to wood, keep the sofa, add warm lighting."
                  value={notes}
                  onChange={handleNotesChange}
                />

                <p className="new-design-char-count" aria-live="polite">
                  {notes.length}/{NOTES_MAX}
                </p>
              </div>

              <div className="new-design-control-group new-design-setup-quality-group">
                <h3 className="new-design-subheading" id="new-design-image-quality-label">
                  <ImageIcon
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Image quality
                </h3>
                <NewDesignSelect
                  id="new-design-image-quality-select"
                  aria-labelledby="new-design-image-quality-label"
                  value={generatedImageQuality}
                  disabled={submitting}
                  onChange={setGeneratedImageQuality}
                  options={GENERATED_IMAGE_QUALITY_OPTIONS}
                />
              </div>

              <div className="new-design-setup-generate-group">
                <button
                  type="button"
                  className="btn btn-primary full-width new-design-generate-btn"
                  onClick={() => void handleSave()}
                  disabled={
                    submitting ||
                    !file ||
                    !naturalWidth ||
                    !hasValidCrop ||
                    editRouteBlocking ||
                    hasInsufficientCredits
                  }
                >
                  <Sparkles size={25} strokeWidth={2} aria-hidden="true" />
                  {generateCta}
                </button>
                {hasInsufficientCredits ? (
                  <div className="new-design-no-credits" role="status">
                    <span>You do not have enough credits.</span>
                    <Link to="/credits" className="btn btn-outline new-design-get-credit-btn">
                      Get credit
                    </Link>
                  </div>
                ) : null}
              </div>
                </>
            </article>

            {showWorkspaceResultPanel ? (
              <DesignResultViewer
                project={currentProject}
                editSourceImageUrl={isEditMode ? editSourcePreviewUrl : null}
                resultViewMode={resultViewMode}
                onDownloadImage={handleDownloadImage}
                onOpenShop={() => setShopOpen(true)}
                onZoomImage={setZoomedImage}
                previewLayout="plain"
                showMobileActions={false}
                showShopOnMobile={false}
              />
            ) : (
            <div
              className={`new-design-workspace-preview${
                croppedPreviewUrl ? ' new-design-workspace-preview--has-image' : ''
              }${showWorkspaceToolGuide ? ' new-design-workspace-preview--has-guide' : ''}${
                submitting ? ' new-design-workspace-preview--submitting' : ''
              }`}
              aria-label="Design preview"
            >
              <div className="new-design-workspace-preview-heading-group">
                {pageTitle}
                {publicSeo?.headingSubtext && !hydratedEditId ? (
                  <p className="new-design-workspace-preview-heading-subtext">{publicSeo.headingSubtext}</p>
                ) : null}
                {showWorkspaceToolGuide ? (
                  <p className="new-design-workspace-preview-tool-hint">{workspaceToolPreview.hint}</p>
                ) : null}
              </div>
              {showWorkspaceToolGuide ? (
                <div className="new-design-workspace-preview-guide">
                  <div className="new-design-workspace-preview-tool-media new-design-workspace-preview-tool-media--vertical-scan">
                    <div className="new-design-workspace-preview-offset-rectangle" aria-hidden="true" />
                    <VerticalAiBeforeAfterSlider
                      beforeImage={workspaceToolPreview.beforeImage}
                      afterImage={workspaceToolPreview.afterImage}
                      beforeAlt={`${workspaceToolPreview.alt} before`}
                      afterAlt={`${workspaceToolPreview.alt} after`}
                      fallbackSrc={workspaceToolPreview.fallbackImage}
                      showLabels
                    />
                  </div>
                </div>
              ) : null}
              {croppedPreviewUrl || submitting ? (
                <div className="new-design-workspace-preview-content">
                  {croppedPreviewUrl ? (
                    <div className="new-design-workspace-preview-media">
                      <img
                        src={croppedPreviewUrl}
                        alt="Cropped room preview"
                        className="new-design-workspace-preview-img"
                        decoding="async"
                      />
                    </div>
                  ) : null}
                  {submitting ? (
                    <GenerateWaitingIndicator submitPhase={submitPhase} />
                  ) : null}
                </div>
              ) : null}
            </div>
            )}
          </section>
          )}
          {publicSeo ? <ToolSeoSections {...publicSeo} /> : null}
          {publicSeo ? <CollapsibleSeoContent {...getSeoContent(publicSeo.path)} /> : null}
        </div>
        {!isInsideAppLayout ? <Footer /> : null}
      </main>
      {zoomedImage ? (
        <div
          className="image-lightbox-backdrop"
          role="presentation"
          onClick={() => setZoomedImage(null)}
        >
          <div
            className="image-lightbox-content"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="image-lightbox-close"
              aria-label="Close image preview"
              onClick={() => setZoomedImage(null)}
            >
              Close
            </button>
            <img
              src={zoomedImage.src}
              alt={zoomedImage.alt}
              className="image-lightbox-img"
              loading="eager"
              decoding="sync"
            />
          </div>
        </div>
      ) : null}
      {reviewPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => {
            if (!submitting) setReviewPopupOpen(false);
          }}
        >
          <div
            id="new-design-review-popup"
            className="new-design-theme-popup new-design-theme-popup--review"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-design-review-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head new-design-review-popup-head">
              <h2 id="new-design-review-popup-title" className="new-design-theme-popup-title">
                <Sparkles size={20} strokeWidth={2.25} aria-hidden />
                Review & Generate
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                disabled={submitting}
                onClick={() => setReviewPopupOpen(false)}
              >
                Cancel
              </button>
            </div>

            <NewDesignGenerateReviewSummary
              selectedRoomType={selectedRoomType}
              selectedStyle={selectedStyle}
              decorationChangeLevel={decorationChangeLevel}
              generatedImageQuality={generatedImageQuality}
              generatedImageCreditCost={generatedImageCreditCost}
            />

            <div className="new-design-selected-panel new-design-selected-panel--in-popup">
              {selectedItems.length === 0 ? (
                <p className="new-design-selected-empty">
                  No items selected. AI will choose furniture automatically.
                </p>
              ) : (
                <ul className="new-design-selected-list new-design-selected-list--in-panel">
                  {selectedItems.map((row) => {
                    const RowIcon = furnitureIconForName(row.name);
                    return (
                      <li key={row.name} className="new-design-selected-row">
                        <div className="new-design-selected-row-main">
                          {RowIcon ? (
                            <span className="new-design-selected-row-icon" aria-hidden="true">
                              <RowIcon size={22} color="#32363F" strokeWidth={2} />
                            </span>
                          ) : null}
                          <span className="new-design-selected-name">{row.name}</span>
                        </div>
                        <div className="new-design-qty-controls">
                          <button
                            type="button"
                            className="btn btn-ghost new-design-qty-btn"
                            aria-label={`Decrease ${row.name}`}
                            disabled={submitting}
                            onClick={() => adjustQuantity(row.name, -1)}
                          >
                            −
                          </button>
                          <span className="new-design-qty-value">{row.quantity}</span>
                          <button
                            type="button"
                            className="btn btn-ghost new-design-qty-btn"
                            aria-label={`Increase ${row.name}`}
                            disabled={submitting || atFurnitureCap}
                            onClick={() => adjustQuantity(row.name, 1)}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            className="new-design-icon-delete"
                            aria-label={`Remove ${row.name}`}
                            disabled={submitting}
                            onClick={() => removeItem(row.name)}
                          >
                            <DeleteIcon size={22} color="currentColor" strokeWidth={2} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="new-design-review-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={submitting}
                onClick={() => setReviewPopupOpen(false)}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary new-design-generate-btn"
                disabled={submitting || hasInsufficientCredits}
                onClick={() => void handleSave()}
              >
                <Sparkles size={25} strokeWidth={2} aria-hidden="true" />
                {submitPhase === 'upload'
                  ? 'Uploading…'
                  : submitPhase === 'generate'
                    ? 'Generating…'
                    : generateCta}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {themeColorsPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => { themeColorSectionRef.current?.flushPendingAdd(); setThemeColorsPopupOpen(false); }}
        >
          <div
            id="new-design-theme-colors-popup"
            className="new-design-theme-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-design-theme-colors-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="new-design-theme-colors-popup-title" className="new-design-theme-popup-title">
                Choose theme colors
              </h2>
              <button
                type="button"
                className="btn btn-primary new-design-theme-popup-close"
                onClick={() => { themeColorSectionRef.current?.flushPendingAdd(); setThemeColorsPopupOpen(false); }}
              >
                Done
              </button>
            </div>
            <DesignHexColorSection
              ref={themeColorSectionRef}
              source={themeColorSource}
              onSourceChange={handleThemeSourceChange}
              maxColors={MAX_CUSTOM_COLORS}
              selectedColors={selectedColors}
              onSelectedColorsChange={handleThemeColorsChange}
              onMarkTouched={markColorsSectionTouched}
              groupAriaLabel="Theme colors"
              showAiHint={false}
            />
          </div>
        </div>
      ) : null}
      {wallColorsPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => { wallColorSectionRef.current?.flushPendingAdd(); setWallColorsPopupOpen(false); }}
        >
          <div
            id="new-design-wall-colors-popup"
            className="new-design-theme-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-design-wall-colors-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="new-design-wall-colors-popup-title" className="new-design-theme-popup-title">
                Choose walls colors
              </h2>
              <button
                type="button"
                className="btn btn-primary new-design-theme-popup-close"
                onClick={() => { wallColorSectionRef.current?.flushPendingAdd(); setWallColorsPopupOpen(false); }}
              >
                Done
              </button>
            </div>
            <DesignHexColorSection
              ref={wallColorSectionRef}
              source={wallColorSource}
              onSourceChange={handleWallSourceChange}
              maxColors={MAX_WALL_COLORS}
              selectedColors={selectedWallColors}
              onSelectedColorsChange={handleWallColorsChange}
              onMarkTouched={markWallsSectionTouched}
              groupAriaLabel="Wall colors"
              showAiHint={false}
            />
          </div>
        </div>
      ) : null}
      {furniturePopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={handleFurnitureCancel}
        >
          <div
            id="new-design-furniture-popup"
            className="new-design-theme-popup new-design-theme-popup--furniture"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-design-furniture-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="new-design-furniture-popup-title" className="new-design-theme-popup-title">
                Extra Furniture
              </h2>
              <div className="new-design-theme-popup-actions">
                <button
                  type="button"
                  className="btn btn-outline new-design-theme-popup-close"
                  onClick={handleFurnitureCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary new-design-theme-popup-close"
                  onClick={handleFurnitureDone}
                >
                  Done
                </button>
              </div>
            </div>
            <div className="new-design-furniture-grid" role="group" aria-label="Furniture items">
              {FURNITURE_CONFIG.map(({ name, Icon }) => {
                const qty = selectedQtyByName.get(name) || 0;
                const active = qty > 0;
                return (
                  <button
                    key={name}
                    type="button"
                    className={`new-design-furniture-tile${active ? ' new-design-furniture-tile--active' : ''}`}
                    onClick={() => handleFurnitureTileClick(name)}
                    disabled={atFurnitureCap}
                    title={name}
                  >
                    <Icon size={40} color="#586174" strokeWidth={2} />
                    <span className="new-design-furniture-tile-label">{name}</span>
                  </button>
                );
              })}
            </div>
            {atFurnitureCap ? (
              <p className="new-design-cap-msg" role="status">
                Maximum {MAX_FURNITURE_QTY} items allowed.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default NewDesignPage;
